import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Heart,
  X,
  ShoppingCart,
  ChevronRight,
  Star,
  Loader2,
  Check,
  Package,
  Sparkles,
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { Separator } from './ui/separator';
import { useFavorites } from '../context/FavoritesContext';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';

// Breadcrumbs Component
const Breadcrumbs = () => {
  return (
    <motion.nav
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      className="flex items-center gap-2 text-sm text-muted-foreground mb-6"
    >
      <Link to="/" className="hover:text-foreground transition-colors">
        Home
      </Link>
      <ChevronRight className="w-4 h-4" />
      <span className="text-foreground font-medium">Favorites</span>
    </motion.nav>
  );
};

// Get badge variant for product tags
const getBadgeVariant = (tag) => {
  switch (tag?.toLowerCase()) {
    case 'offers':
      return 'bg-foreground text-background';
    case 'more':
      return 'bg-muted text-muted-foreground';
    case 'store':
      return 'bg-foreground text-background';
    case 'featured':
      return 'bg-success text-success-foreground';
    case 'new':
      return 'bg-badge-new text-primary-foreground';
    default:
      return 'bg-muted text-muted-foreground';
  }
};

// Favorite Item Card Component
const FavoriteCard = ({ item, index, onRemove, onMoveToCart, onViewProduct }) => {
  const [isRemoving, setIsRemoving] = useState(false);

  const handleRemove = () => {
    setIsRemoving(true);
    setTimeout(() => {
      onRemove(item.id);
    }, 200);
  };

  const handleMoveToCart = () => {
    onMoveToCart(item);
    toast.success('Added to cart!', {
      description: `${item.name} has been moved to your cart.`,
    });
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 30, scale: 0.95 }}
      animate={{
        opacity: isRemoving ? 0 : 1,
        y: 0,
        scale: isRemoving ? 0.9 : 1,
      }}
      exit={{ opacity: 0, scale: 0.9, transition: { duration: 0.3 } }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
      className="relative group"
    >
      <Card className="bg-card border-0 rounded-[24px] overflow-hidden shadow-product transition-all duration-300 hover:shadow-product-hover hover:-translate-y-2">
        {/* Remove Button - Top Right */}
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={handleRemove}
          className="absolute top-4 right-4 z-20 w-8 h-8 rounded-full bg-background/80 backdrop-blur-sm flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors shadow-sm"
        >
          <X className="w-4 h-4" />
        </motion.button>

        {/* Badge */}
        {item.brandTag && (
          <div className="absolute top-4 left-4 z-10">
            <Badge
              className={`${getBadgeVariant(item.brandTag)} rounded-full px-3 py-1 text-xs font-medium`}
            >
              {item.brandTag}
            </Badge>
          </div>
        )}

        <CardContent className="p-6 pb-4">
          {/* Product Image */}
          <div
            className="relative aspect-square bg-muted/30 rounded-2xl overflow-hidden mb-4 cursor-pointer"
            onClick={() => onViewProduct(item.id)}
          >
            <motion.img
              src={item.image}
              alt={item.name}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
            />
            {/* Favorite Heart Overlay */}
            <div className="absolute bottom-3 right-3">
              <motion.div
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 0.3 }}
                className="w-10 h-10 rounded-full bg-background/90 backdrop-blur-sm flex items-center justify-center shadow-md"
              >
                <Heart className="w-5 h-5 fill-destructive text-destructive" />
              </motion.div>
            </div>
          </div>

          {/* Product Info */}
          <div className="space-y-3">
            <div className="flex items-start justify-between gap-2">
              <h3
                className="font-semibold text-foreground text-base leading-tight line-clamp-2 cursor-pointer hover:text-primary transition-colors"
                onClick={() => onViewProduct(item.id)}
              >
                {item.name}
              </h3>
              <span className="font-bold text-foreground text-lg whitespace-nowrap">
                ${item.price.toFixed(2)}
              </span>
            </div>

            {/* Rating */}
            {item.rating && (
              <div className="flex items-center gap-1.5">
                <Star className="w-4 h-4 fill-warning text-warning" />
                <span className="text-sm font-medium text-foreground">{item.rating}</span>
                <span className="text-sm text-muted-foreground">
                  ({item.reviews} Reviews)
                </span>
              </div>
            )}

            {/* Quick Actions */}
            <div className="flex items-center gap-2 pt-2">
              <motion.div whileTap={{ scale: 0.95 }} className="flex-1">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full rounded-full border-border hover:bg-muted transition-colors"
                  onClick={handleRemove}
                >
                  Remove
                </Button>
              </motion.div>
              <motion.div whileTap={{ scale: 0.9 }} className="flex-1">
                <Button
                  size="sm"
                  className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-colors gap-1"
                  onClick={handleMoveToCart}
                >
                  <ShoppingCart className="w-3.5 h-3.5" />
                  Add to Cart
                </Button>
              </motion.div>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

// Favorites Summary Component
const FavoritesSummary = ({ count, totalValue, onMoveAllToCart, onClearAll }) => {
  const [isMoving, setIsMoving] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleMoveAll = async () => {
    if (count === 0) return;
    setIsMoving(true);
    await new Promise((resolve) => setTimeout(resolve, 1200));
    setIsMoving(false);
    setIsSuccess(true);
    onMoveAllToCart();
    setTimeout(() => {
      setIsSuccess(false);
    }, 2000);
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 30 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5, delay: 0.2 }}
      className="bg-card rounded-[32px] p-6 lg:p-8 shadow-lifted sticky top-24"
    >
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center">
          <Heart className="w-5 h-5 text-destructive fill-destructive" />
        </div>
        <h2 className="font-display font-semibold text-xl text-foreground">
          Favorites Summary
        </h2>
      </div>

      {/* Stats */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">Total Items</span>
          <span className="font-semibold text-foreground">{count}</span>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">Total Value</span>
          <span className="font-bold text-xl text-foreground">${totalValue.toFixed(2)}</span>
        </div>

        <Separator />

        {/* Move All to Cart Button */}
        <motion.button
          onClick={handleMoveAll}
          disabled={isMoving || isSuccess || count === 0}
          className={`w-full h-14 rounded-full font-medium text-base flex items-center justify-center gap-2 transition-all ${
            isSuccess
              ? 'bg-success text-success-foreground'
              : 'bg-foreground text-background hover:bg-foreground/90'
          } disabled:opacity-50 disabled:cursor-not-allowed`}
          whileTap={{ scale: 0.98 }}
        >
          <AnimatePresence mode="wait">
            {isMoving ? (
              <motion.div
                key="loading"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <Loader2 className="w-5 h-5 animate-spin" />
              </motion.div>
            ) : isSuccess ? (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                className="flex items-center gap-2"
              >
                <Check className="w-5 h-5" />
                <span>Added to Cart!</span>
              </motion.div>
            ) : (
              <motion.div
                key="default"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex items-center gap-2"
              >
                <ShoppingCart className="w-5 h-5" />
                <span>Move All to Cart</span>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.button>

        {/* Clear All Button */}
        {count > 0 && (
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={onClearAll}
            className="w-full h-12 rounded-full border-2 border-border font-medium text-sm flex items-center justify-center gap-2 text-muted-foreground hover:text-destructive hover:border-destructive/50 hover:bg-destructive/5 transition-colors"
          >
            <X className="w-4 h-4" />
            <span>Clear All Favorites</span>
          </motion.button>
        )}
      </div>

      {/* Tip */}
      {count > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-6 p-4 bg-muted/50 rounded-xl"
        >
          <div className="flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-warning shrink-0 mt-0.5" />
            <p className="text-sm text-muted-foreground">
              <span className="font-medium text-foreground">Pro tip:</span> Items in your
              favorites don&apos;t reserve stock. Add to cart to secure your purchase!
            </p>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

// Empty Favorites Component
const EmptyFavorites = () => {
  const navigate = useNavigate();

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="flex flex-col items-center justify-center py-20 text-center"
    >
      <motion.div
        initial={{ scale: 0.8 }}
        animate={{ scale: 1 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="relative w-32 h-32 mb-6"
      >
        <div className="absolute inset-0 bg-destructive/10 rounded-full animate-pulse" />
        <div className="relative w-full h-full bg-muted rounded-full flex items-center justify-center">
          <Heart className="w-16 h-16 text-muted-foreground" />
        </div>
      </motion.div>

      <h2 className="font-display font-semibold text-2xl text-foreground mb-2">
        No favorites yet
      </h2>
      <p className="text-muted-foreground max-w-sm mb-8">
        Start exploring and tap the heart icon to save items you love. They&apos;ll all
        appear here!
      </p>

      <motion.div whileTap={{ scale: 0.98 }}>
        <Button
          onClick={() => navigate('/')}
          className="rounded-full px-8 h-12 bg-foreground text-background hover:bg-foreground/90 gap-2"
        >
          <Package className="w-4 h-4" />
          Discover Products
        </Button>
      </motion.div>
    </motion.div>
  );
};

// Main Favorites Page Component
const FavoritesPage = () => {
  const navigate = useNavigate();
  const { favorites, favoritesCount, removeFromFavorites, clearFavorites } = useFavorites();
  const { addToCart, cartItemsCount, isCartAnimating } = useCart();

  // Calculate total value of favorites
  const totalValue = favorites.reduce((sum, item) => sum + item.price, 0);

  const handleRemove = (productId) => {
    removeFromFavorites(productId);
    toast.success('Removed from favorites');
  };

  const handleMoveToCart = (item) => {
    addToCart({
      id: item.id,
      name: item.name,
      price: item.price,
      image: item.image,
      selectedSize: item.availableSizes?.[0] || null,
      selectedColor: item.colors?.[0] || null,
      quantity: 1,
      brand: item.brand,
      rating: item.rating,
      reviews: item.reviews,
    });
  };

  const handleMoveAllToCart = () => {
    favorites.forEach((item) => {
      addToCart({
        id: item.id,
        name: item.name,
        price: item.price,
        image: item.image,
        selectedSize: item.availableSizes?.[0] || null,
        selectedColor: item.colors?.[0] || null,
        quantity: 1,
        brand: item.brand,
        rating: item.rating,
        reviews: item.reviews,
      });
    });
    toast.success(`Added ${favorites.length} items to cart!`);
  };

  const handleClearAll = () => {
    clearFavorites();
    toast.success('All favorites cleared');
  };

  const handleViewProduct = (productId) => {
    navigate(`/product/${productId}`);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link to="/">
              <motion.div
                className="flex items-center gap-2 cursor-pointer"
                whileHover={{ scale: 1.02 }}
              >
                <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                  <span className="text-primary-foreground font-display font-bold text-sm">
                    S
                  </span>
                </div>
                <span className="font-display font-bold text-xl text-foreground">
                  StuffLab
                </span>
              </motion.div>
            </Link>

            {/* Right Actions */}
            <div className="flex items-center gap-4">
              {/* Cart */}
              <motion.div
                animate={isCartAnimating ? { scale: [1, 1.3, 1] } : {}}
                transition={{ duration: 0.4 }}
              >
                <Button
                  variant="ghost"
                  size="icon"
                  className="relative rounded-full"
                  onClick={() => navigate('/cart')}
                >
                  <ShoppingCart className="w-5 h-5" />
                  {cartItemsCount > 0 && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      key={cartItemsCount}
                    >
                      <Badge className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center bg-warning text-foreground text-xs rounded-full">
                        {cartItemsCount > 99 ? '99+' : cartItemsCount}
                      </Badge>
                    </motion.div>
                  )}
                </Button>
              </motion.div>

              {/* Favorites */}
              <Button variant="ghost" size="icon" className="relative rounded-full">
                <Heart className="w-5 h-5 fill-destructive text-destructive" />
                {favoritesCount > 0 && (
                  <Badge className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center bg-destructive text-destructive-foreground text-xs rounded-full">
                    {favoritesCount}
                  </Badge>
                )}
              </Button>

              {/* User */}
              <div className="w-8 h-8 rounded-full bg-muted overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100"
                  alt="User"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-10">
        {/* Page Title */}
        <motion.h1
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="font-display font-bold text-4xl md:text-5xl text-foreground mb-2"
        >
          Your Favorites
        </motion.h1>

        {/* Breadcrumbs */}
        <Breadcrumbs />

        {favorites.length === 0 ? (
          <EmptyFavorites />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr,320px] gap-8 lg:gap-12">
            {/* Favorites Grid */}
            <div>
              <motion.div
                layout
                className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6"
              >
                <AnimatePresence mode="popLayout">
                  {favorites.map((item, index) => (
                    <FavoriteCard
                      key={item.id}
                      item={item}
                      index={index}
                      onRemove={handleRemove}
                      onMoveToCart={handleMoveToCart}
                      onViewProduct={handleViewProduct}
                    />
                  ))}
                </AnimatePresence>
              </motion.div>

              {/* Bottom Actions */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="flex flex-wrap items-center gap-4 mt-8"
              >
                <Button
                  variant="outline"
                  className="rounded-full"
                  onClick={() => navigate('/')}
                >
                  Continue Shopping
                </Button>
              </motion.div>
            </div>

            {/* Summary Sidebar - Desktop */}
            <div className="hidden lg:block">
              <FavoritesSummary
                count={favoritesCount}
                totalValue={totalValue}
                onMoveAllToCart={handleMoveAllToCart}
                onClearAll={handleClearAll}
              />
            </div>
          </div>
        )}
      </main>

      {/* Mobile Summary - Fixed at bottom */}
      {favorites.length > 0 && (
        <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-background border-t border-border p-4 shadow-lifted z-40">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm text-muted-foreground">
                  {favoritesCount} items saved
                </p>
                <p className="font-bold text-xl text-foreground">
                  ${totalValue.toFixed(2)}
                </p>
              </div>
              <motion.div whileTap={{ scale: 0.98 }}>
                <Button
                  className="rounded-full px-6 h-12 bg-foreground text-background hover:bg-foreground/90 gap-2"
                  onClick={() => {
                    handleMoveAllToCart();
                    navigate('/cart');
                  }}
                >
                  <ShoppingCart className="w-4 h-4" />
                  Move All to Cart
                </Button>
              </motion.div>
            </div>
          </div>
        </div>
      )}

      {/* Add padding at bottom for mobile fixed bar */}
      {favorites.length > 0 && <div className="lg:hidden h-28" />}
    </div>
  );
};

export default FavoritesPage;
